import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import {
  LocalStorageActions,
  LocalStorageManagementFeature,
} from '@qtek/core/local-storage-management';
import { BackendResponse, HTTP_BASE_URL, Language } from '@qtek/shared/models';
import { isNonNullable, isNullable } from '@qtek/shared/utils';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs';

import { MetaCoreFeature } from '@qtek/libs/meta-core';
import {
  DebugTranslationsDI,
  LanguagesArray,
  passWhenTrue,
} from '../models/translation-core.model';
import { TranslationCoreActions } from '../store/translation-core.actions';
import { TranslationCoreFeature } from '../store/translation-core.reducer';

// export function isNonNullable<T>(value: T | undefined | null): value is T {
//   return value !== null && value !== undefined;
// }
//
// export function isNullable<T>(value: T | undefined | null): value is undefined | null {
//   return value === null || value === undefined;
// }

@Injectable({
  providedIn: 'root',
})
export class TranslationCoreService {
  private readonly isTranslationsInitialized$ = new BehaviorSubject(false);

  private readonly cachedTranslationsSubject = new BehaviorSubject<
    Record<Language, Record<string, string>> | undefined
  >(undefined);
  private readonly cachedLangSubject = new BehaviorSubject<
    Language | undefined
  >(undefined);

  public get selectedLang(): Language {
    return this.getCachedLanguage() ?? 'en_US';
  }

  public selectedLang$ = this.getCachedLanguageSubject();

  // cache translations
  private translationsState$ = this.store
    .select(TranslationCoreFeature.selectTranslations)
    .pipe(
      distinctUntilChanged(),
      passWhenTrue(this.isTranslationsInitialized$),
      tap(translations => {
        this.cachedTranslationsSubject.next(translations);
      }),
      shareReplay(1),
      takeUntilDestroyed()
    );

  // cache language
  private languageState$ = this.store
    .select(TranslationCoreFeature.selectLang)
    .pipe(
      passWhenTrue(this.isTranslationsInitialized$),
      // filter initial fast changes
      debounceTime(50),
      distinctUntilChanged(),
      filter(lang => isNonNullable(lang)),
      tap(lang => {
        this.cachedLangSubject.next(lang);
      }),
      shareReplay(1),
      takeUntilDestroyed()
    );

  private currentTranslations$ = combineLatest([
    this.translationsState$,
    this.languageState$,
  ]).pipe(
    passWhenTrue(this.isTranslationsInitialized$),
    map(([translations, lang]) => translations?.[lang]),
    shareReplay(1),
    takeUntilDestroyed()
  );

  /** Emits when translations or language change. Use in pipes to trigger re-render (e.g. columnHeader). */
  readonly translationsOrLang$ = this.currentTranslations$;

  constructor(
    private http: HttpClient,
    private store: Store,
    @Inject(HTTP_BASE_URL) private httpBaseUrl: string,
    @Optional() @Inject(DebugTranslationsDI) private debugTranslation: boolean
  ) {
    this.initializeTranslations().subscribe();

    this.currentTranslations$
      .pipe(
        filter(
          translations =>
            isNullable(translations) ||
            (typeof translations === 'object' &&
              Object.keys(translations).length === 0)
        ),
        switchMap(() =>
          this.store.select(TranslationCoreFeature.selectLang).pipe(take(1))
        ),
        switchMap(lang => {
          console.log('fetch lang: ', lang);

          return this.fetchTranslations(lang);
        }),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  private initializeTranslations(): Observable<unknown> {
    const isLocalStorageLoaded$ = this.store.select(
      LocalStorageManagementFeature.selectIsInitialized
    );

    const localStorageTranslations$ = this.store.select(
      LocalStorageManagementFeature.selectI18n
    );

    const wsVersion$ = this.store
      .select(MetaCoreFeature.selectWebsocketVersion)
      .pipe(filter(v => isNonNullable(v)));

    return combineLatest([localStorageTranslations$, wsVersion$]).pipe(
      passWhenTrue(isLocalStorageLoaded$),
      take(1),
      tap(([translationsStr, wsVersion]) => {
        // Handle no storage translations
        if (isNullable(translationsStr)) {
          // Set default language
          this.store.dispatch(
            TranslationCoreActions.setLanguage({
              language: this.getUserBrowserLanguageCode(),
            })
          );
          // Set default translations
          this.store.dispatch(
            TranslationCoreActions.getCachedTranslationsSuccess({
              translations: {
                en_US: {},
                pl_PL: {},
                es_US: {},
              },
            })
          );
        } else {
          const { translations, lang, version } = JSON.parse(translationsStr);

          // Handle version mismatch
          if (wsVersion !== version) {
            // Delete translations from local storage
            this.store.dispatch(
              LocalStorageActions.deleteLocalStorageByKey({
                key: 'i18n',
              })
            );
            // Set default translations
            this.store.dispatch(
              TranslationCoreActions.getCachedTranslationsSuccess({
                translations: {
                  en_US: {},
                  pl_PL: {},
                  es_US: {},
                },
              })
            );
          } else {
            // Set translations from local storage
            this.store.dispatch(
              TranslationCoreActions.getCachedTranslationsSuccess({
                translations,
              })
            );
          }

          // Set language from local storage
          this.store.dispatch(
            TranslationCoreActions.setLanguage({
              language: lang,
            })
          );
        }

        this.store.dispatch(
          TranslationCoreActions.setVersion({ version: wsVersion })
        );

        this.store.dispatch(TranslationCoreActions.saveTranslationsToCache());

        this.isTranslationsInitialized$.next(true);
      })
    );
  }

  private fetchTranslations(
    lang: Language
  ): Observable<BackendResponse<Record<string, string>>> {
    return this.http
      .get<
        BackendResponse<Record<string, string>>
      >(`${this.httpBaseUrl}/api/v1/service/i18n?lng=${lang}`)
      .pipe(
        tap(({ res }) => {
          this.store.dispatch(
            TranslationCoreActions.getTranslationsSuccess({
              translations: res,
              lang,
            })
          );
        })
      );
  }

  public setLanguage(lang: Language) {
    this.isTranslationsInitialized$
      .pipe(
        filter(Boolean),
        take(1),
        tap(() =>
          this.store.dispatch(
            TranslationCoreActions.setLanguage({ language: lang })
          )
        )
      )
      .subscribe();
  }

  public getTranslations(keys: string[]): Observable<any> {
    return this.currentTranslations$.pipe(
      filter(
        translations =>
          isNonNullable(translations) ||
          (typeof translations === 'object' &&
            Object.keys(translations).length > 0)
      ),
      map(translations => {
        return keys.reduce((acc, key) => {
          acc[key] = translations[key] ?? (this.debugTranslation ? key : '');
          return acc;
        }, {} as any);
      })
    );
  }

  public getTranslation(
    key: string,
    options?: { params?: Array<any> }
  ): Observable<string> {
    return this.currentTranslations$.pipe(
      filter(translations => {
        if (isNullable(translations) || typeof translations !== 'object')
          return false;
        // stops on the first key, no allocation, O(1) when the object has keys
        for (const _ in translations as object) return true;
        return false;
      }),
      map(translations => {
        const translation = translations?.[key];

        if (!translation) {
          return this.debugTranslation ? key : '';
        }
        return options?.params
          ? this.interpolate(translation, options.params)
          : translation;
      })
    );
  }

  /** Fallback labels when the backend does not provide a translation. */
  private static readonly FALLBACK_LABELS: Record<string, string> = {
    GNR_NOTE_AUTHOR: 'Note Author',
    GNR_BENCHMARKING_FIELDS: 'Benchmarking fields',
    GNR_BENCHMARKING_FIELDS_DESC:
      'Fields marked with this icon are used in deal benchmarking and comparisons',
    GNR_CMP_ALLOW_ALPHA: 'Company allows Alpha features',
  };

  public instant(key: string, params?: Array<any>): string {
    const translations = this.getCachedTranslations();
    const lang = this.getCachedLanguage() ?? 'en_US';
    const translation = translations?.[lang]?.[key];

    if (!translation) {
      const fallback = TranslationCoreService.FALLBACK_LABELS[key];
      if (fallback)
        return params ? this.interpolate(fallback, params) : fallback;
      return this.debugTranslation ? key : '';
    }

    return params ? this.interpolate(translation, params) : translation;
  }

  /**
   * Bulk synchronous translation for many keys. Uses cached translations.
   * Returns a map key -> translated string (or fallback / key / '' per key).
   */
  public instantMany(keys: string[]): Record<string, string> {
    const translations = this.getCachedTranslations();
    const lang = this.getCachedLanguage() ?? 'en_US';
    const map = translations?.[lang];
    const result: Record<string, string> = {};
    for (const key of keys) {
      const t = map?.[key];
      if (t != null) {
        result[key] = t;
      } else {
        const fallback = TranslationCoreService.FALLBACK_LABELS[key];
        result[key] = fallback ?? (this.debugTranslation ? key : '');
      }
    }
    return result;
  }

  saveTranslationToCache(): void {
    combineLatest([
      this.store.select(TranslationCoreFeature.selectTranslations),
      this.store.select(TranslationCoreFeature.selectLang),
      this.store.select(TranslationCoreFeature.selectVersion),
    ])
      .pipe(
        passWhenTrue(this.isTranslationsInitialized$),
        filter(([translations, lang, version]) => {
          return (
            isNonNullable(translations) &&
            isNonNullable(lang) &&
            isNonNullable(version)
          );
        }),
        take(1),
        tap(([translations, lang, version]) => {
          this.store.dispatch(
            LocalStorageActions.saveToLocalStorageByKey({
              key: 'i18n',
              value: JSON.stringify({
                translations,
                lang,
                version,
              }),
            })
          );
        })
      )
      .subscribe();
  }

  // TODO: Fix duplication
  public setupNoNetworkTranslations(): void {
    const isLocalStorageLoaded$ = this.store.select(
      LocalStorageManagementFeature.selectIsInitialized
    );

    const localStorageTranslations$ = this.store.select(
      LocalStorageManagementFeature.selectI18n
    );

    localStorageTranslations$
      .pipe(
        passWhenTrue(isLocalStorageLoaded$),
        take(1),
        tap(translationsStr => {
          // Handle no storage translations
          if (isNullable(translationsStr)) {
            // Set default language
            this.store.dispatch(
              TranslationCoreActions.setLanguage({
                language: this.getUserBrowserLanguageCode(),
              })
            );
            // Set default translations
            this.store.dispatch(
              TranslationCoreActions.getCachedTranslationsSuccess({
                translations: {
                  en_US: {},
                  pl_PL: {},
                  es_US: {},
                },
              })
            );
          } else {
            const { translations, lang, version } = JSON.parse(translationsStr);

            // Set translations from local storage
            this.store.dispatch(
              TranslationCoreActions.getCachedTranslationsSuccess({
                translations,
              })
            );

            // Set language from local storage
            this.store.dispatch(
              TranslationCoreActions.setLanguage({
                language: lang,
              })
            );

            this.store.dispatch(
              TranslationCoreActions.saveTranslationsToCache()
            );

            this.isTranslationsInitialized$.next(true);
          }
        })
      )
      .subscribe();
  }

  getCachedTranslations():
    | Record<'en_US' | 'es_US' | 'pl_PL', Record<string, string>>
    | undefined {
    return this.cachedTranslationsSubject.getValue();
  }

  private getCachedLanguage(): 'en_US' | 'es_US' | 'pl_PL' | undefined {
    return this.cachedLangSubject.getValue();
  }

  private getCachedLanguageSubject(): BehaviorSubject<
    'en_US' | 'es_US' | 'pl_PL' | undefined
  > {
    return this.cachedLangSubject;
  }

  public getLanguages$(): Observable<Language[]> {
    return of(LanguagesArray);
  }

  private interpolate(expression: string, params?: Array<any>): string {
    if (typeof expression !== 'string' || !params || !Array.isArray(params)) {
      return expression;
    }

    return params.reduce(
      (message, value, index) => message.replace(`{${index + 1}}`, value),
      expression
    );
  }

  private getUserBrowserLanguageCode(): Language {
    const userLang = navigator.language;
    const code = userLang[0] + userLang[1];
    let languageCode: Language = 'en_US';

    switch (code) {
      case 'pl': {
        languageCode = 'pl_PL';
        break;
      }
      case 'es': {
        languageCode = 'es_US';
        break;
      }
      default:
        break;
    }
    return languageCode;
  }
}
